import { useEffect, useState } from 'react';

import { DatePicker, Empty, Input } from 'antd';
import moment from 'moment';
import { T } from 'react-translator-component';

import {
  initials,
  isRemoteUpdate,
  previousVersion,
  updatedByName,
  versionCell,
} from './listFormater';
import {
  Breadcrumb,
  Container,
  EmptyWrap,
  FilterCard,
  PageHead,
  SummaryCard,
  SummaryRow,
  TableCard,
} from './styled';

import { getFirmwareUpdateHistory } from '../../../hooks/firmware';
import { getTenants } from '../../../hooks/tenants';
import { parserTenantsForSelect } from '../../../utils/stations/stationSelect';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import { Pagination } from '../../molecules/Pagination/Pagination';
import { Select } from '../../organisms/Select';

const { RangePicker } = DatePicker;
const PAGE_SIZE = 10;
const MAX_RANGE_DAYS = 90;

const FirmwareHistoryList = () => {
  const [list, setList] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  // Filtro por tenant ('' = todos os do subtree do usuário).
  const [tenantPk, setTenantPk] = useState<any>('');
  const [tenantList, setTenantList] = useState<any[]>([]);
  // Default = última semana (7 dias).
  const [pickerValue, setPickerValue] = useState<any>(() => [
    moment().subtract(7, 'days'),
    moment(),
  ]);
  // Seleção parcial (usada por disabledDate para limitar o range a 90 dias).
  const [pickerDates, setPickerDates] = useState<any>(null);
  const [fromDate, setFromDate] = useState<string>(() =>
    moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
  );
  const [toDate, setToDate] = useState<string>(() =>
    moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getFirmwareUpdateHistory({
        fromDate,
        toDate,
        searchInput: search,
        tenantPk,
        page: currentPage,
        size: PAGE_SIZE,
      });
      setList(res?.results || []);
      setCount(res?.count || 0);
      setSummary(res?.summary || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, fromDate, toDate, search, tenantPk]);

  // Carrega os tenants do subtree do usuário para o filtro.
  useEffect(() => {
    (async () => {
      const t = await getTenants();
      setTenantList(t?.list || []);
    })();
  }, []);

  const onTenantChange = (value: any) => {
    setTenantPk(value ?? '');
    setCurrentPage(1);
  };

  // Bloqueia datas futuras e limita a seleção a um range de 90 dias.
  const disabledDate = (current: any) => {
    if (!current) return false;
    if (current > moment().endOf('day')) return true;
    if (!pickerDates || pickerDates.length === 0) return false;
    const start = pickerDates[0];
    const end = pickerDates[1];
    const tooLate = start && current.diff(start, 'days') >= MAX_RANGE_DAYS;
    const tooEarly = end && end.diff(current, 'days') >= MAX_RANGE_DAYS;
    return !!tooLate || !!tooEarly;
  };

  const onRangeChange = (values: any) => {
    if (values && values[0] && values[1]) {
      setPickerValue(values);
      setFromDate(values[0].clone().startOf('day').format('YYYY-MM-DD HH:mm:ss'));
      setToDate(values[1].clone().endOf('day').format('YYYY-MM-DD HH:mm:ss'));
    } else {
      // Limpou -> volta para o default (última semana).
      const def: [any, any] = [moment().subtract(7, 'days'), moment()];
      setPickerValue(def);
      setFromDate(def[0].clone().startOf('day').format('YYYY-MM-DD HH:mm:ss'));
      setToDate(def[1].clone().endOf('day').format('YYYY-MM-DD HH:mm:ss'));
    }
    setCurrentPage(1);
  };

  const onSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const from = count === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, count);

  return (
    <Container>
      <PageHead>
        <span className='ico'>⟲</span>
        <span className='page-title'>{T('Firmware Update History')}</span>
      </PageHead>
      <Breadcrumb>
        <b>{T('Firmware History')}</b>
      </Breadcrumb>

      <FilterCard>
        <div className='field tenant-field'>
          <label>{T('Account')}</label>
          <Select
            value={tenantPk}
            options={parserTenantsForSelect(tenantList)}
            action={onTenantChange}
            ordered
          />
        </div>
        <div className='field'>
          <label>{T('Station')}</label>
          <Input.Search
            allowClear
            placeholder={T('Search by station')}
            onSearch={onSearch}
          />
        </div>
        <div className='field'>
          <label>{T('Period')}</label>
          <RangePicker
            format='DD/MM/YYYY'
            allowClear
            value={pickerValue}
            disabledDate={disabledDate}
            onOpenChange={(open: boolean) => {
              if (open) setPickerDates([null, null]);
            }}
            onCalendarChange={(vals: any) => setPickerDates(vals)}
            onChange={onRangeChange}
          />
        </div>
      </FilterCard>

      <SummaryRow>
        <SummaryCard>
          <span className='label'>{T('Updates in period')}</span>
          <span className='value'>
            {summary?.updatesInPeriod ?? count ?? 0}
          </span>
          <span className='sub'>{T('Total records')}</span>
        </SummaryCard>
        <SummaryCard>
          <span className='label'>{T('Last update')}</span>
          <span className='value gray'>
            {summary?.lastUpdate?.version || '—'}
          </span>
          <span className='sub'>
            {summary?.lastUpdate
              ? `${summary.lastUpdate.chargerName || ''} ${
                  summary.lastUpdate.date
                    ? moment(summary.lastUpdate.date).format('DD/MM/YYYY HH:mm')
                    : ''
                }`.trim()
              : ''}
          </span>
        </SummaryCard>
      </SummaryRow>

      <TableCard>
        <div className='table-card-head'>
          <h3>
            {T('Update records')}
            <span className='count'>
              {count} {T('results')}
            </span>
          </h3>
        </div>

        {loading ? (
          <ListSkeleton style={{ padding: 20 }} />
        ) : list.length === 0 ? (
          <EmptyWrap>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={T('No records found')}
            />
          </EmptyWrap>
        ) : (
          <>
            <div className='table-scroll'>
              <table>
                <thead>
                  <tr>
                    <th>{T('Charger')}</th>
                    <th>{T('Update date')}</th>
                    <th>{T('MCU previous version')}</th>
                    <th>{T('MCU current version')}</th>
                    <th>{T('Gateway previous version')}</th>
                    <th>{T('Gateway current version')}</th>
                    <th>{T('Updated by')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((ev: any, index: number) => {
                    const remote = isRemoteUpdate(ev);
                    const name = updatedByName(ev);
                    // Baseline = estação sem histórico: mostra só a versão atual do MCU
                    // (charge_box), sem "atualizado por". Coluna vazia = "—" (não "Reiniciar").
                    return (
                      <tr
                        key={ev.id ?? index}
                        className={ev.baseline ? 'baseline-row' : ''}
                      >
                        <td>
                          <div className='charger-cell'>
                            <div>
                              <div className='charger-name'>
                                {ev.chargerName || '—'}
                                {ev.baseline && (
                                  <span className='baseline-tag'>
                                    {T('Initial version')}
                                  </span>
                                )}
                              </div>
                              <div className='charger-station'>
                                {ev.stationName || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {ev.updatedAt
                            ? moment(ev.updatedAt).format('DD/MM/YYYY HH:mm')
                            : '—'}
                        </td>
                        <td>
                          {versionCell(
                            previousVersion(ev, ev.mcuOldVersion, ev.mcuNewVersion),
                          )}
                        </td>
                        <td>{versionCell(ev.mcuNewVersion)}</td>
                        <td>
                          {versionCell(
                            previousVersion(
                              ev,
                              ev.gatewayOldVersion,
                              ev.gatewayNewVersion,
                            ),
                          )}
                        </td>
                        <td>
                          {/* Baseline não tem versão de gateway (só vem ao vivo no
                              gatewayInfo): prompt de reiniciar para capturá-la. */}
                          {versionCell(
                            ev.gatewayNewVersion,
                            ev.baseline && !ev.gatewayNewVersion,
                          )}
                        </td>
                        <td>
                          {ev.baseline ? (
                            <span className='version-dash'>—</span>
                          ) : (
                            <div className='user-cell'>
                              <div
                                className={`user-chip ${remote ? 'system' : ''}`}
                              >
                                {remote ? 'OTA' : initials(name)}
                              </div>
                              {name}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='table-footer'>
              <div>
                {T('Showing')} {from}-{to} {T('of')} {count}
              </div>
              <Pagination
                currentPage={currentPage}
                totalCount={count}
                pageSize={PAGE_SIZE}
                onPageChange={(page: number) => setCurrentPage(page)}
              />
            </div>
          </>
        )}
      </TableCard>
    </Container>
  );
};

export default FirmwareHistoryList;
